import { useMemo, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, Funnel, Prohibit, Sparkle, ArrowsClockwise, Check } from "@phosphor-icons/react";
import { computeKeywordPools } from "@/lib/csv";
import { enrichKeywords, errText } from "@/lib/api";

const INTENT_META = {
    commercial: { label: "Commercial", cls: "bg-amber-100 text-amber-900 border-amber-300" },
    transactional: { label: "Transactional", cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
    informational: { label: "Informational", cls: "bg-sky-100 text-sky-900 border-sky-300" },
    navigational: { label: "Navigational", cls: "bg-violet-100 text-violet-900 border-violet-300" },
};

const SOURCE_META = {
    custom: { label: "Custom", cls: "bg-ink text-white" },
    competitor: { label: "Competitor", cls: "bg-red-100 text-red-900 border border-red-300" },
    seed: { label: "Seed", cls: "bg-klein text-white" },
    related: { label: "Related", cls: "bg-neutral-200 text-ink border border-neutral-300" },
    inferred: { label: "Inferred", cls: "bg-yellow-100 text-yellow-900 border border-yellow-300" },
};

const fmtNum = (n) => {
    if (n === null || n === undefined) return "—";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
};

const fmtBid = (low, high, symbol) => {
    if (low === null || low === undefined || high === null || high === undefined) return "—";
    return `${symbol}${low.toFixed(2)}–${symbol}${high.toFixed(2)}`;
};

export const KeywordRefinementPanel = ({ result, onApplyRefined }) => {
    const [enriched, setEnriched] = useState(null); // Array<EnrichedKeyword>
    const [loading, setLoading] = useState(false);
    const [brandText, setBrandText] = useState("");
    const [intents, setIntents] = useState({
        commercial: true,
        transactional: true,
        informational: true,
        navigational: true,
    });
    const [sortBy, setSortBy] = useState("volume"); // "volume" | "bid" | "keyword"

    // Build pools + attach source tag; only compute when no enriched list yet.
    const seededList = useMemo(() => {
        if (!result) return [];
        const { customPool, competitorPool, seedPool, relatedPool, inferredPool } =
            computeKeywordPools(result, {});
        const rows = [
            ...customPool.map((k) => ({ keyword: k, source: "custom" })),
            ...competitorPool.map((k) => ({ keyword: k, source: "competitor" })),
            ...seedPool.map((k) => ({ keyword: k, source: "seed" })),
            ...relatedPool.map((k) => ({ keyword: k, source: "related" })),
            ...inferredPool.map((k) => ({ keyword: k, source: "inferred" })),
        ];
        return rows;
    }, [result]);

    const handleEnrich = async () => {
        if (!seededList.length) {
            toast.error("No keywords to enrich yet");
            return;
        }
        setLoading(true);
        const toastId = toast.loading("Fetching search volumes + classifying intent…");
        try {
            const data = await enrichKeywords({
                keywords: seededList,
                country: result.country || "us",
                city: result.city || "",
                classify_intent: true,
            });
            setEnriched(data || []);
            toast.success(`Enriched ${data.length} keywords`, { id: toastId });
        } catch (e) {
            const msg = errText(e, "Enrichment failed");
            toast.error(msg, { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    // Parse brand exclude list — comma OR newline OR semicolon separated, case-insensitive substring match.
    const brandTerms = useMemo(() => {
        return brandText
            .split(/[\n,;]/)
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);
    }, [brandText]);

    const filtered = useMemo(() => {
        if (!enriched) return [];
        return enriched.filter((row) => {
            const kw = row.keyword.toLowerCase();
            if (brandTerms.some((b) => kw.includes(b))) return false;
            if (row.intent && !intents[row.intent]) return false;
            return true;
        });
    }, [enriched, brandTerms, intents]);

    const sorted = useMemo(() => {
        const arr = [...filtered];
        if (sortBy === "volume") {
            arr.sort((a, b) => (b.search_volume || 0) - (a.search_volume || 0));
        } else if (sortBy === "bid") {
            arr.sort((a, b) => (b.high_bid_local || 0) - (a.high_bid_local || 0));
        } else {
            arr.sort((a, b) => a.keyword.localeCompare(b.keyword));
        }
        return arr;
    }, [filtered, sortBy]);

    const symbol = enriched?.[0]?.currency_symbol || "$";
    const currency = enriched?.[0]?.currency || "USD";

    const removedCount = enriched ? enriched.length - filtered.length : 0;

    const handleApply = () => {
        if (!filtered.length) {
            toast.error("Refined list is empty — nothing to apply");
            return;
        }
        onApplyRefined?.(filtered.map((r) => r.keyword));
        toast.success(`Applied ${filtered.length} keywords to Campaign Builder`);
    };

    return (
        <div
            data-testid="keyword-refinement-panel"
            className="border-t border-neutral-200 px-5 py-6"
        >
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Funnel size={18} weight="bold" className="text-klein" />
                    <span className="font-mono uppercase tracking-widest text-[11px] font-bold text-ink">
                        Keyword Refinement · DataForSEO + AI Intent
                    </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        data-testid="btn-enrich-keywords"
                        onClick={handleEnrich}
                        disabled={loading || !seededList.length}
                        className="brutal-btn border border-ink bg-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5 disabled:opacity-40"
                    >
                        {loading ? (
                            <ArrowsClockwise size={13} weight="bold" className="animate-spin" />
                        ) : (
                            <MagnifyingGlass size={13} weight="bold" />
                        )}
                        {enriched ? "Re-Enrich" : "Enrich Keywords"}
                    </button>
                    {enriched && (
                        <button
                            data-testid="btn-apply-refined"
                            onClick={handleApply}
                            className="brutal-btn border border-klein bg-klein text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                        >
                            <Check size={13} weight="bold" />
                            Apply to Builder
                        </button>
                    )}
                </div>
            </div>

            {!enriched && (
                <p className="text-sm text-neutral-600 leading-relaxed max-w-2xl">
                    Enrich the {seededList.length} candidate keywords with monthly search volume,
                    Google Ads competition, low/high top-of-page bid estimates (in{" "}
                    <span className="font-bold">
                        {(result?.country || "US").toUpperCase()}
                    </span>{" "}
                    currency), and AI-classified search intent. Then filter out competitor/brand
                    terms and non-buying intents before exporting.
                </p>
            )}

            {enriched && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                        {/* Brand Exclude Filter */}
                        <div className="border border-neutral-300 bg-white p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Prohibit size={14} weight="bold" className="text-red-600" />
                                <span className="font-mono uppercase tracking-widest text-[10px] font-bold text-ink">
                                    Brand Exclude Filter
                                </span>
                            </div>
                            <textarea
                                data-testid="brand-exclude-textarea"
                                value={brandText}
                                onChange={(e) => setBrandText(e.target.value)}
                                placeholder="mario, rentokil, checkatrade..."
                                className="w-full min-h-[60px] text-sm border border-neutral-300 px-2 py-1.5 font-mono focus:outline-none focus:border-ink"
                            />
                            <div className="mt-1 text-[11px] text-neutral-500 font-mono">
                                {brandTerms.length} term{brandTerms.length === 1 ? "" : "s"} · comma / newline / semicolon separated
                            </div>
                        </div>

                        {/* AI Intent Filter */}
                        <div className="border border-neutral-300 bg-white p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkle size={14} weight="bold" className="text-klein" />
                                <span className="font-mono uppercase tracking-widest text-[10px] font-bold text-ink">
                                    AI Intent Filter
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {Object.entries(INTENT_META).map(([key, meta]) => {
                                    const active = intents[key];
                                    return (
                                        <button
                                            key={key}
                                            data-testid={`intent-toggle-${key}`}
                                            onClick={() =>
                                                setIntents((p) => ({ ...p, [key]: !p[key] }))
                                            }
                                            className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest font-bold border transition ${
                                                active
                                                    ? meta.cls + " border-transparent"
                                                    : "bg-white text-neutral-400 border-neutral-300 line-through"
                                            }`}
                                        >
                                            {meta.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mt-2 text-[11px] text-neutral-500 font-mono">
                                Click a tag to include / exclude that intent.
                            </div>
                        </div>
                    </div>

                    {/* Stats bar */}
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-widest text-neutral-600">
                            <span data-testid="refined-count">
                                <span className="text-ink font-black">{filtered.length}</span> shown
                            </span>
                            <span className="text-neutral-400">·</span>
                            <span>
                                <span className="text-red-700 font-black">{removedCount}</span> filtered
                            </span>
                            <span className="text-neutral-400">·</span>
                            <span>
                                bids in <span className="font-black text-ink">{currency}</span>
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="font-mono uppercase tracking-widest text-[10px] text-neutral-500">
                                Sort:
                            </span>
                            {[
                                { k: "volume", l: "Volume" },
                                { k: "bid", l: "Bid" },
                                { k: "keyword", l: "A→Z" },
                            ].map((opt) => (
                                <button
                                    key={opt.k}
                                    data-testid={`sort-${opt.k}`}
                                    onClick={() => setSortBy(opt.k)}
                                    className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest font-bold border transition ${
                                        sortBy === opt.k
                                            ? "bg-ink text-white border-ink"
                                            : "bg-white text-ink border-neutral-300 hover:border-ink"
                                    }`}
                                >
                                    {opt.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="border border-neutral-300 overflow-hidden">
                        <div className="max-h-[420px] overflow-y-auto thin-scroll">
                            <table
                                data-testid="refined-keywords-table"
                                className="w-full text-sm"
                            >
                                <thead className="bg-neutral-100 sticky top-0 z-10">
                                    <tr className="text-left">
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold">
                                            Keyword
                                        </th>
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold">
                                            Source
                                        </th>
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                            Volume
                                        </th>
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                            Avg Bid
                                        </th>
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold">
                                            Competition
                                        </th>
                                        <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold whitespace-nowrap min-w-[130px]">
                                            Intent
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorted.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={6}
                                                className="px-3 py-8 text-center text-neutral-500 text-sm"
                                            >
                                                All keywords filtered out — loosen the brand or intent filters.
                                            </td>
                                        </tr>
                                    )}
                                    {sorted.map((row) => {
                                        const src = SOURCE_META[row.source] || SOURCE_META.related;
                                        const intent = INTENT_META[row.intent] || null;
                                        return (
                                            <tr
                                                key={row.keyword}
                                                data-testid={`kw-row-${row.keyword}`}
                                                className="border-t border-neutral-200 hover:bg-neutral-50"
                                            >
                                                <td className="px-3 py-2 text-ink font-medium">
                                                    {row.keyword}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span
                                                        className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest font-bold ${src.cls}`}
                                                    >
                                                        {src.label}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-ink">
                                                    {fmtNum(row.search_volume)}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-ink whitespace-nowrap">
                                                    {fmtBid(row.low_bid_local, row.high_bid_local, row.currency_symbol)}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span
                                                        className={`text-[10px] font-mono uppercase tracking-widest font-bold ${
                                                            row.competition === "HIGH"
                                                                ? "text-red-700"
                                                                : row.competition === "MEDIUM"
                                                                ? "text-amber-700"
                                                                : row.competition === "LOW"
                                                                ? "text-emerald-700"
                                                                : "text-neutral-400"
                                                        }`}
                                                    >
                                                        {row.competition || "—"}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    {intent ? (
                                                        <span
                                                            className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest font-bold border ${intent.cls}`}
                                                        >
                                                            {intent.label}
                                                        </span>
                                                    ) : (
                                                        <span className="text-neutral-400 text-[10px]">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
